import {
  faCameraSecurity,
  faChartNetwork,
  faChartSimple,
  faCommentLines,
  faDiagramProject,
  faGear,
  faKey,
  faMessageDots,
  faSitemap,
  faSliders,
  faTireRugged,
  faUserGroup,
  faUsers,
  faMapPin,
} from '@fortawesome/pro-regular-svg-icons';
import { getRoute } from '@vision/ui/constants';
import { AccountType, FeatureStatus, Role } from '@vision/ui/enums';
import {
  CustomIconAdmin,
  CustomIconCati,
  CustomIconCatiList,
  CustomIconContacts,
  CustomIconCustomerJourneyMap,
  CustomIconDashboard,
  CustomIconFlow,
  CustomIconHeadSideHeadPhones,
  CustomIconPlanner,
  CustomIconTextAnalysis,
  CustomIconTicket,
} from '@vision/ui/icons';
import { Account, NavigationLink, Permission, User } from '@vision/ui/interfaces';
import { checkPermission, ensureArray, insertIfArray } from '@vision/ui/utils';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { changeSecuritySettingsModalVisibleAction } from '../store';

import { useAccount } from './useAccount';
import { useAppDispatch } from './useAppDispatch';
import { checkAccountType } from './useCheckAccountType';
import { checkRole } from './useCheckRole';
import { useExAccount } from './useExAccount';
import { useEnvironmentUrl } from './useEnvironmentUrl';
import { usePermissions } from './usePermissions';
import { useUser } from './useUser';

export function checkRoleAndPermission(
  item: NavigationLink,
  user: User,
  account: Account,
  userPermissions: Permission,
) {
  return [
    { value: item.permission, valid: checkPermission(item.permission, userPermissions) },
    { value: item.role, valid: checkRole(user, item.role) },
    { value: item.accountType, valid: checkAccountType(account, item.accountType) },
  ]
    .filter(({ value }) => {
      if (!value) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return true;
    })
    .every(({ valid }) => valid);
}

function checkPermissionRecursively(
  item: NavigationLink,
  user: User,
  account: Account,
  userPermissions: Permission,
): NavigationLink {
  return {
    ...item,
    items: ensureArray(item.items)
      .filter((item) => checkRoleAndPermission(item, user, account, userPermissions))
      .map((child) => {
        if (ensureArray(child.items).length) {
          return checkPermissionRecursively(child, user, account, userPermissions);
        }

        return child;
      }),
  } as NavigationLink;
}

function checkIfNavigationLinkIsValid(item: NavigationLink) {
  return item.items?.length > 0 || !!item.href || typeof item.onClick === 'function';
}

function checkIfNavigationLinkIsValidRecursively(item: NavigationLink): NavigationLink {
  return {
    ...item,
    items: ensureArray(item.items)
      .filter(checkIfNavigationLinkIsValid)
      .map((child) => {
        if (ensureArray(child.items).length) {
          return checkIfNavigationLinkIsValidRecursively(child);
        }

        return child;
      }),
  } as NavigationLink;
}

export function useNavigationLinks(): NavigationLink[] {
  const user = useUser();
  const account = useAccount();
  const isExAccount = useExAccount();
  const userPermissions = usePermissions();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation(['routes']);
  const releaseNotesUrl = useEnvironmentUrl('RELEASE_NOTES');

  const translateRoute = useCallback((key: string) => t(`routes:${key}`), []);

  const cxSettingsMenu = useMemo(
    () =>
      [
        {
          id: 'team',
          label: translateRoute('team'),
          icon: faUsers,
          items: [getRoute('Users'), getRoute('AuditLogs'), getRoute('ActionLogs')],
        },
        {
          id: 'feedback',
          label: translateRoute('feedback'),
          icon: faCommentLines,
          items: [
            getRoute('ContactPolicy'),
            getRoute('TagGroups'),
            getRoute('QuickResponses'),
            getRoute('ReviewWidgets'),
            getRoute('KnowledgeBases'),
            getRoute('SchemaFields'),
            getRoute('TicketSchemaFields'),
          ],
        },
        {
          id: 'automation',
          label: translateRoute('automation'),
          icon: faGear,
          items: [
            getRoute('Escalations'),
            getRoute('AutomatedJobs'),
            getRoute('ScheduleJobs'),
            getRoute('EmailAutomations'),
            {
              ...getRoute('TicketManagerSettings'),
              label: translateRoute('ticketManagerSettings'),
              itemsHidden: true,
              subLabel: translateRoute('ticketManagerProjects'),
              items: [getRoute('TicketManagerStatusDefinition'), getRoute('TicketManagerStatusTransitions')],
            },
          ],
        },
        {
          id: 'organizationalStructure',
          label: translateRoute('organizationalStructure'),
          icon: faSitemap,
          items: [getRoute('Units'), getRoute('TreeView')],
        },
        {
          id: 'accountSettings',
          label: translateRoute('accountSettings'),
          icon: faSliders,
          accountType: [AccountType.ACCOUNT, AccountType.ACCOUNT_EX],
          permission: 'settings.accountSettings',
          items: [
            getRoute('RolesAndPermissions'),
            getRoute('AuthorizedApps'),
            getRoute('SmsProvider'),
            getRoute('MailProvider'),
            getRoute('TelephoneProvider'),
            getRoute('FeedbackProvider'),
            getRoute('FtpAccount'),
            getRoute('RemoteDatabaseAccount'),
            getRoute('SamlConfiguration'),
            getRoute('SmtpConfiguration'),
            getRoute('ReportTheme'),
          ],
        },
        {
          id: 'uberAdmin',
          label: translateRoute('uberAdmin'),
          icon: faKey,
          role: Role.UBER_ADMIN,
          items: [
            getRoute('PisanoMembers'),
            getRoute('ReportWizard'),
            getRoute('Accounts'),
            getRoute('AccountStatistics'),
            getRoute('Benchmarking'),
            getRoute('Verticals'),
            getRoute('LdapConfiguration'),
          ],
        },
        {
          id: 'securitySettings',
          label: translateRoute('securitySettings'),
          icon: faCameraSecurity,
          permission: 'settings.securitySettings',
          onClick: () => {
            dispatch(changeSecuritySettingsModalVisibleAction(true));
          },
        },
      ] as NavigationLink[],
    [],
  );

  const exSettingsMenu = useMemo(
    () =>
      [
        {
          id: 'team',
          label: translateRoute('team'),
          icon: faUsers,
          items: [
            getRoute('Users'),
            getRoute('AuditLogs'),
            getRoute('ActionLogs'),
            getRoute('ContactPolicy'),
            getRoute('TagGroups'),
            getRoute('QuickResponses'),
            getRoute('ReviewWidgets'),
            getRoute('KnowledgeBases'),
            getRoute('Escalations'),
            getRoute('AutomatedJobs'),
            getRoute('ELCSurveyTriggerLogs'),
            getRoute('ScheduleJobs'),
            getRoute('EmailAutomations'),
            getRoute('Units'),
            getRoute('TreeView'),
            getRoute('JourneyMaps'),
            {
              ...getRoute('TicketManagerSettings'),
              label: translateRoute('ticketManagerSettings'),
              itemsHidden: true,
              subLabel: translateRoute('ticketManagerProjects'),
              items: [getRoute('TicketManagerStatusDefinition'), getRoute('TicketManagerStatusTransitions')],
            },
          ],
        },
        {
          id: 'accountSettings',
          label: translateRoute('accountSettings'),
          icon: faSliders,
          accountType: [AccountType.ACCOUNT, AccountType.ACCOUNT_EX],
          permission: 'settings.accountSettings',
          items: [
            getRoute('RolesAndPermissions'),
            getRoute('SmsProvider'),
            getRoute('MailProvider'),
            getRoute('TelephoneProvider'),
            getRoute('HrisProvider'),
            getRoute('FeedbackProvider'),
            getRoute('FtpAccount'),
            getRoute('RemoteDatabaseAccount'),
            getRoute('SamlConfiguration'),
            getRoute('SmtpConfiguration'),
            getRoute('AuthorizedApps'),
            getRoute('ReportTheme'),
          ],
        },
        {
          id: 'uberAdmin',
          label: translateRoute('uberAdmin'),
          icon: faKey,
          role: Role.UBER_ADMIN,
          items: [
            getRoute('PisanoMembers'),
            getRoute('ReportWizard'),
            getRoute('Accounts'),
            getRoute('AccountStatistics'),
            getRoute('Benchmarking'),
            getRoute('Verticals'),
            getRoute('LdapConfiguration'),
            getRoute('SchemaFields'),
          ],
        },
        {
          id: 'securitySettings',
          label: translateRoute('securitySettings'),
          icon: faCameraSecurity,
          permission: 'settings.securitySettings',
          onClick: () => {
            dispatch(changeSecuritySettingsModalVisibleAction(true));
          },
        },
      ] as NavigationLink[],
    [],
  );

  return useMemo(
    () =>
      (
        [
          ...insertIfArray(isExAccount, [
            {
              icon: <CustomIconDashboard />,
              ...getRoute('EXDashboard'),
            },
            {
              icon: <CustomIconHeadSideHeadPhones />,
              ...getRoute('EXReports'),
              category: 'analytics',
            },
            {
              icon: faChartSimple,
              ...getRoute('OldReports'),
              hideFromNavigation: true,
            },
          ]),
          ...insertIfArray(!isExAccount, [
            {
              ...getRoute('Reports'),
              icon: faChartSimple,
              featureStatus: FeatureStatus.BETA,
              subLabel: translateRoute('reportsSubLabel'),
              defaultVisible: true,
              category: 'analytics',
              featureDescription: translateRoute('reportsFeatureDescription'),
            },
            {
              icon: faChartSimple,
              ...getRoute('OldReports'),
              hideFromNavigation: true,
            },
            {
              icon: <CustomIconHeadSideHeadPhones />,
              ...getRoute('Insights'),
              category: 'analytics',
            },
          ]),
          ...insertIfArray(!isExAccount, [
            {
              ...getRoute('JourneyMaps'),
              icon: <CustomIconCustomerJourneyMap />,
              category: 'customerEngagement',
            },
            {
              id: 'catiMenu',
              icon: <CustomIconCati />,
              label: translateRoute('Cati'),
              category: 'customerEngagement',
              items: [
                {
                  icon: <CustomIconCati />,
                  ...getRoute('Cati'),
                  label: translateRoute('catiCalls'),
                },
                {
                  icon: <CustomIconCatiList />,
                  ...getRoute('CatiList'),
                },
              ],
            },
          ]),
          {
            id: 'textAnalytics',
            label: translateRoute('textAnalytics'),
            icon: <CustomIconTextAnalysis />,
            category: 'analytics',
            items: [
              {
                ...getRoute('IntelligentTags'),
                icon: faSitemap,
                itemsHidden: true,
                items: [getRoute('Dictionary'), getRoute('TagTraining')],
              },
              {
                ...getRoute('ContentCategories'),
                icon: faCommentLines,
              },
              {
                ...getRoute('AIModels'),
                icon: faTireRugged,
              },
              {
                ...getRoute('FeedbackAnalysisList'),
                icon: faUserGroup,
              },
              {
                ...getRoute('AtaDashboard'),
                icon: faDiagramProject,
              },
            ],
          },
          {
            icon: faMessageDots,
            ...getRoute('Inbox'),
            defaultVisible: true,
            category: 'customerEngagement',
          },
          {
            ...getRoute('Profile'),
            hideFromNavigation: true,
          },
          {
            icon: <CustomIconFlow />,
            ...getRoute('Flows'),
            defaultVisible: true,
            category: 'dataCollection',
          },
          {
            icon: faChartNetwork,
            ...getRoute('Channels'),
            defaultVisible: true,
            category: 'dataCollection',
          },
          {
            ...getRoute('Workflows'),
            icon: faDiagramProject,
            itemsHidden: true,
            subLabel: translateRoute('workflowsSubLabel'),
            items: [getRoute('WorkflowsRunHistory')],
            category: 'automation',
          },
          {
            ...getRoute('Planner'),
            icon: <CustomIconPlanner />,
            category: 'automation',
          },
          {
            ...getRoute('TicketManager'),
            href: undefined,
            permission: undefined,
            label: translateRoute('ticketManager'),
            icon: <CustomIconTicket />,
            featureStatus: FeatureStatus.NEW,
            featureDescription: translateRoute('ticketManagerFeatureDescription'),
            items: [getRoute('TicketManagerPool'), getRoute('TicketManagerQueue'), getRoute('TicketManagerReports')],
            category: 'automation',
          },
          {
            label: translateRoute('releaseNotes'),
            href: releaseNotesUrl,
            icon: faMapPin,
            featureStatus: FeatureStatus.NEW,
            category: 'external',
          },
          ...insertIfArray(!isExAccount, [
            {
              ...getRoute('Contacts'),
              icon: <CustomIconContacts />,
              itemsHidden: true,
              subLabel: translateRoute('contactsSubLabel'),
              items: [getRoute('Business'), getRoute('Segments'), getRoute('ContactsList')],
              category: 'customerEngagement',
            },
          ]),
          ...insertIfArray(isExAccount, {
            ...getRoute('EXEmployees'),
            icon: faSitemap,
          }),
          {
            id: 'settings',
            icon: <CustomIconAdmin />,
            label: translateRoute('settings'),
            permission: 'settings',
            items: isExAccount ? exSettingsMenu : cxSettingsMenu,
          },
        ] as NavigationLink[]
      )
        // İlk seviyeyi filtrele
        .filter((link) => checkRoleAndPermission(link, user, account, userPermissions))
        // Kalan seviyeler içerisinde recursive filtrele
        .map((link) => checkPermissionRecursively(link, user, account, userPermissions))
        .filter(checkIfNavigationLinkIsValid)
        .map(checkIfNavigationLinkIsValidRecursively)
        .map((link) => {
          const addParentToNavigationLinksRecursively: (
            item: NavigationLink,
            parent: NavigationLink,
          ) => NavigationLink = (item: NavigationLink, parent: NavigationLink) => {
            return {
              ...item,
              items: ensureArray(item.items).map((child) => addParentToNavigationLinksRecursively(child, item)),
              parentNavigationLink: parent,
            } as NavigationLink;
          };

          const resolvedLink =
            link.id === 'TicketManager'
              ? {
                  ...link,
                  href: link.items[0]?.href,
                }
              : link;

          return {
            ...resolvedLink,
            items: resolvedLink.items.map((child) => addParentToNavigationLinksRecursively(child, resolvedLink)),
          } as NavigationLink;
        }),
    [i18n.language, releaseNotesUrl, user, userPermissions, isExAccount],
  );
}
